// @ts-nocheck
import { getLocales } from 'expo-localization';
import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';
import { en } from './locales/en';
import { tr } from './locales/tr';

export type Language = 'en' | 'tr';
export type TranslationKeys = typeof en;

const translations: Record<Language, TranslationKeys> = {
  en,
  tr,
};

const LANGUAGE_KEY = 'user_language';
const EXPLICIT_LANGUAGE_KEY = 'user_language_explicit'; // Track if user manually selected language

class I18n {
  private currentLanguage: Language = 'en';
  private listeners: Set<() => void> = new Set();
  private initialized: boolean = false;

  constructor() {
    // For web, initialize synchronously
    if (Platform.OS === 'web') {
      this.initializeSync();
    } else {
      this.initialize();
    }
  }

  private initializeSync() {
    try {
      // Highest priority: dev-country override (web only)
      const devLang = this.getLanguageFromDevOverride();
      if (devLang) {
        this.currentLanguage = devLang;
        // Save but do NOT mark explicit; this is an override for testing/runtime
        localStorage.setItem(LANGUAGE_KEY, devLang);
        this.initialized = true;
        return;
      }

      // Web: synchronous localStorage access
      const saved = localStorage.getItem(LANGUAGE_KEY);
      if (saved && this.isValidLanguage(saved)) {
        this.currentLanguage = saved as Language;
        this.initialized = true;
        return;
      }

      // Try async country detection for web (won't block render)
      this.detectLanguageFromCountry().then(lang => {
        if (lang && this.currentLanguage !== lang) {
          this.currentLanguage = lang;
          this.notifyListeners();
        }
      });

      // Immediately fall back to device locale detection for initial render
      const deviceLocales = getLocales();
      const deviceLanguage = deviceLocales[0]?.languageCode;

      if (deviceLanguage && this.isValidLanguage(deviceLanguage)) {
        this.currentLanguage = deviceLanguage;
      } else {
        this.currentLanguage = 'en'; // Default fallback
      }

      this.initialized = true;
    } catch (error) {
      console.warn('Failed to initialize language:', error);
      this.currentLanguage = 'en';
      this.initialized = true;
    }
  }

  private async initialize() {
    try {
      // Highest priority: dev-country override (mobile envs can set via env/local storage bridges)
      const devLang = this.getLanguageFromDevOverride();
      if (devLang) {
        this.currentLanguage = devLang;
        await this.saveLanguage(devLang); // Save but do NOT mark explicit
        this.initialized = true;
        this.notifyListeners();
        return;
      }

      // Check if user has explicitly set a language preference
      const hasExplicitLanguage = await this.hasExplicitLanguagePreference();
      
      if (hasExplicitLanguage) {
        // User has explicitly chosen a language, respect their choice
        const savedLanguage = await this.getSavedLanguage();
        if (savedLanguage) {
          this.currentLanguage = savedLanguage;
          this.initialized = true;
          this.notifyListeners();
          return;
        }
      }

      // Try to detect language from country (via pricing API)
      // This will auto-update language based on location
      const countryLanguage = await this.detectLanguageFromCountry();
      if (countryLanguage) {
        this.currentLanguage = countryLanguage;
        // Save it but mark as auto-detected (not explicit user choice)
        await this.saveLanguage(countryLanguage);
        this.initialized = true;
        this.notifyListeners();
        return;
      }

      // Fall back to device locale detection
      const deviceLocales = getLocales();
      const deviceLanguage = deviceLocales[0]?.languageCode;

      if (deviceLanguage && this.isValidLanguage(deviceLanguage)) {
        this.currentLanguage = deviceLanguage;
      } else {
        this.currentLanguage = 'en'; // Default fallback
      }

      this.initialized = true;
      this.notifyListeners();
    } catch (error) {
      console.warn('Failed to initialize language:', error);
      this.currentLanguage = 'en';
      this.initialized = true;
      this.notifyListeners();
    }
  }

  // Map dev-country override to language; TR -> tr, else en
  private getLanguageFromDevOverride(): Language | null {
    try {
      if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
        const dc = (localStorage.getItem('dev_country') || '').toString().trim().toUpperCase();
        if (dc) {
          return dc === 'TR' ? 'tr' : 'en';
        }
      }
      // Optionally support EXPO_PUBLIC_DEV_COUNTRY for native debug sessions
      const envDev = (process.env.EXPO_PUBLIC_DEV_COUNTRY || '').toString().trim().toUpperCase();
      if (envDev) {
        return envDev === 'TR' ? 'tr' : 'en';
      }
    } catch {
      // ignore
    }
    return null;
  }

  private async detectLanguageFromCountry(): Promise<Language | null> {
    try {
      const API_URL = process.env.EXPO_PUBLIC_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/api/billing/pricing`, {
        headers: {
          // Include dev country override if set
          ...(Platform.OS === 'web' && typeof localStorage !== 'undefined' && localStorage.getItem('dev_country')
            ? { 'X-Dev-Country': localStorage.getItem('dev_country')! }
            : {}
          )
        }
      });
      
      if (!response.ok) return null;
      
      const data = await response.json();
      const country = data.country;
      
      // Map country to language
      if (country === 'TR') {
        console.log('[i18n] Detected Turkey, setting language to Turkish');
        return 'tr';
      }
      
      // Default to English for all other countries
      console.log(`[i18n] Detected country: ${country || 'unknown'}, setting language to English`);
      return 'en';
    } catch (error) {
      console.warn('[i18n] Failed to detect language from country:', error);
      return null;
    }
  }

  private async getSavedLanguage(): Promise<Language | null> {
    try {
      if (Platform.OS === 'web') {
        const saved = localStorage.getItem(LANGUAGE_KEY);
        return saved && this.isValidLanguage(saved) ? (saved as Language) : null;
      } else {
        const saved = await SecureStore.getItemAsync(LANGUAGE_KEY);
        return saved && this.isValidLanguage(saved) ? (saved as Language) : null;
      }
    } catch {
      return null;
    }
  }

  private async hasExplicitLanguagePreference(): Promise<boolean> {
    try {
      if (Platform.OS === 'web') {
        return localStorage.getItem(EXPLICIT_LANGUAGE_KEY) === 'true';
      } else {
        const explicit = await SecureStore.getItemAsync(EXPLICIT_LANGUAGE_KEY);
        return explicit === 'true';
      }
    } catch {
      return false;
    }
  }

  private async setExplicitLanguagePreference(explicit: boolean): Promise<void> {
    try {
      if (Platform.OS === 'web') {
        localStorage.setItem(EXPLICIT_LANGUAGE_KEY, explicit.toString());
      } else {
        await SecureStore.setItemAsync(EXPLICIT_LANGUAGE_KEY, explicit.toString());
      }
    } catch (error) {
      console.warn('Failed to set explicit language preference:', error);
    }
  }

  private async saveLanguage(language: Language): Promise<void> {
    try {
      if (Platform.OS === 'web') {
        localStorage.setItem(LANGUAGE_KEY, language);
      } else {
        await SecureStore.setItemAsync(LANGUAGE_KEY, language);
      }
    } catch (error) {
      console.warn('Failed to save language:', error);
    }
  }

  private isValidLanguage(lang: string): lang is Language {
    return lang === 'en' || lang === 'tr';
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener());
  }

  getCurrentLanguage(): Language {
    return this.currentLanguage;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  async setLanguage(language: Language, explicit: boolean = true): Promise<void> {
    if (this.currentLanguage !== language) {
      this.currentLanguage = language;
      await this.saveLanguage(language);
      if (explicit) {
        // Mark this as an explicit user choice
        await this.setExplicitLanguagePreference(true);
      }
      this.notifyListeners();
    }
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // Translation function with type safety
  t(key: string, defaultOrVars?: string | Record<string, any>, vars?: Record<string, any>): string {
    let defaultValue: string | undefined;
    let values: Record<string, any> | undefined;

    if (typeof defaultOrVars === 'object' && defaultOrVars !== null) {
      values = defaultOrVars as Record<string, any>;
    } else {
      defaultValue = defaultOrVars as string | undefined;
      values = vars;
    }

    try {
      const keys = key.split('.');
      let current: any = translations[this.currentLanguage];

      const interpolate = (tpl: string): string => {
        if (!values) return tpl;
        let out = tpl;
        for (const [placeholder, value] of Object.entries(values)) {
          const pattern = new RegExp(`{{\\s*${placeholder}\\s*}}`, 'g');
          out = out.replace(pattern, String(value));
        }
        return out;
      };

      for (const k of keys) {
        if (current && typeof current === 'object' && k in current) {
          current = current[k];
        } else {
          // Fallback to English
          current = translations.en;
          for (const k of keys) {
            if (current && typeof current === 'object' && k in current) {
              current = current[k];
            } else {
              return interpolate(defaultValue || key);
            }
          }
          break;
        }
      }

      const template = typeof current === 'string' ? current : (defaultValue || key);
      return interpolate(template);
    } catch {
      return defaultValue || key;
    }
  }

  // Get all available languages
  getAvailableLanguages(): { code: Language; name: string; nativeName: string }[] {
    return [
      { code: 'en', name: 'English', nativeName: 'English' },
      { code: 'tr', name: 'Turkish', nativeName: 'Türkçe' },
    ];
  }

  // Get language-specific AI prompt prefixes
  getAIPromptPrefix(): string {
    switch (this.currentLanguage) {
      case 'tr':
        return 'Lütfen Türkçe yanıt verin. ';
      case 'en':
      default:
        return 'Please respond in English. ';
    }
  }

  // Check if language has been set (for showing language selection)
  async hasLanguageBeenSet(): Promise<boolean> {
    const saved = await this.getSavedLanguage();
    return saved !== null;
  }
}

// Create singleton instance
export const i18n = new I18n();

// Export translation function for easier usage
export const t = (key: string, defaultValue?: string) => i18n.t(key, defaultValue);
